import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonVariant,
  IconButton,
  IconButtonVariant,
  Radio
} from "shared/components";
import { cx } from "shared/utils";

export type TemplateRouteQuestion = {
  question: string;
  template_name: string;
  options: { key: string; label: string; template_name: string | null }[];
};

const splitVariantName = (name: string, parentName: string) => {
  const prefix = `${parentName} - `;
  return name.startsWith(prefix) && name.length > prefix.length
    ? { prefix, suffix: name.slice(prefix.length) }
    : null;
};

const TemplateName = ({
  name,
  parentName
}: {
  name: string;
  parentName: string;
}) => {
  const parts = splitVariantName(name, parentName);

  if (!parts) return <span className="min-w-0 truncate">{name}</span>;

  return (
    <span className="flex min-w-0 whitespace-pre">
      <span className="min-w-8 truncate">{parts.prefix}</span>
      <span className="min-w-0 max-w-[75%] shrink-0 truncate">
        {parts.suffix}
      </span>
    </span>
  );
};

/**
 * First screen of a manual application whose template asks a routing question:
 * the property manager answers it on the applicant's behalf, which decides the
 * template the application runs on.
 */
export const ChooseTemplateScreen = ({
  templateRoute,
  answer,
  onChange,
  onContinue,
  onClose
}: {
  templateRoute: TemplateRouteQuestion;
  answer?: string;
  onChange: (answer: string) => void;
  onContinue: () => void;
  onClose: () => void;
}) => {
  return (
    <div
      data-fit-content
      className="flex max-h-[calc(100dvh-100px)] flex-col overflow-hidden rounded-lg"
    >
      <div className="flex shrink-0 items-start gap-4 border-b border-alpha-40 pb-5 pl-12 pr-6 pt-7">
        <div className="min-w-0 flex-1 space-y-1">
          <h1 className="text-xl font-bold text-content-1000">
            Choose application template
          </h1>
          <p className="text-sm text-content-400">
            Ask the applicant this question. Their answer decides which
            screening template the application uses.
          </p>
        </div>
        <IconButton
          HugeIcon={Cancel01Icon}
          variant={IconButtonVariant.TEXT_BASE}
          onClick={onClose}
          aria-label="Close"
        />
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-12 pb-7 pt-6">
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-content-400">
            Question
          </p>
          <h2 className="break-words text-lg font-semibold text-content-1000">
            {templateRoute.question}
          </h2>
        </div>

        <fieldset className="min-w-0 space-y-2">
          <legend className="sr-only">Applicant&apos;s answer</legend>
          {templateRoute.options.map((option) => {
            const isMain = option.template_name == null;
            const templateName =
              option.template_name ?? templateRoute.template_name;
            const checked = answer === option.key;

            return (
              <Radio
                key={option.key}
                id={`template-route-${option.key}`}
                name="template-route-answer"
                value={option.key}
                checked={checked}
                onChange={() => onChange(option.key)}
                className={cx(
                  "flex w-full min-w-0 cursor-pointer items-center gap-3.5 rounded-lg border px-4 py-3.5",
                  checked
                    ? "border-primary-500 bg-primary-20 ring-1 ring-primary-500"
                    : "border-alpha-100 hover:bg-alpha-20"
                )}
                labelClassName="min-w-0 flex-1"
                label={
                  <span className="block min-w-0 space-y-0.5">
                    <span
                      title={option.label}
                      className="line-clamp-2 break-words text-base font-semibold text-content-1000"
                    >
                      {option.label}
                    </span>
                    <span
                      title={templateName}
                      className="flex min-w-0 items-center gap-1.5 text-sm text-content-400"
                    >
                      <span className="shrink-0">Screened with</span>
                      <span className="flex min-w-0 font-medium text-content-700">
                        <TemplateName
                          name={templateName}
                          parentName={templateRoute.template_name}
                        />
                      </span>
                      <Badge
                        size={BadgeSize.SM}
                        variant={
                          isMain ? BadgeVariant.BASE : BadgeVariant.PRIMARY
                        }
                        className="shrink-0"
                      >
                        {isMain ? "Main" : "Variant"}
                      </Badge>
                    </span>
                  </span>
                }
              />
            );
          })}
        </fieldset>
      </div>

      <div className="shrink-0 space-y-2 border-t border-alpha-40 py-4 pl-12 pr-6">
        <div className="flex justify-end gap-3">
          <Button variant={ButtonVariant.BASE} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            disabled={answer === undefined}
            onClick={onContinue}
          >
            Continue
          </Button>
        </div>
        <p className="flex items-center justify-end gap-1.5 text-sm font-medium text-warning-700">
          <HugeiconsIcon
            icon={AlertCircleIcon}
            size={16}
            className="shrink-0 text-warning-600"
          />
          The answer can’t be changed after you continue.
        </p>
      </div>
    </div>
  );
};
